import { useState } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Card } from "@/components/ui/card";

interface EventPhotoGalleryProps {
  photos: string[];
  eventTitle: string;
}

const EventPhotoGallery = ({ photos, eventTitle }: EventPhotoGalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  if (!photos || photos.length === 0) return null;

  const handlePrevious = () => {
    setSelectedIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setSelectedIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-4">Event Photos ({photos.length})</h3>
      
      {/* Thumbnail Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {photos.map((photo, index) => (
          <Dialog key={index} open={isOpen && selectedIndex === index} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <button
                onClick={() => {
                  setSelectedIndex(index);
                  setIsOpen(true);
                }}
                className="relative aspect-square rounded-lg overflow-hidden group cursor-pointer"
              >
              <img
                  src={photo}
                  alt={`${eventTitle} photo ${index + 1}`}
                  className="w-full h-full object-cover transition-transform group-hover:scale-110 photo-signature"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl p-0 bg-black">
              <div className="relative">
                {/* Close button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-4 right-4 z-10 bg-black/50 text-white hover:bg-black/70"
                  onClick={() => setIsOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>

                {/* Navigation buttons */}
                {photos.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 text-white hover:bg-black/70"
                      onClick={handlePrevious}
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 text-white hover:bg-black/70"
                      onClick={handleNext}
                    >
                      <ChevronRight className="h-6 w-6" />
                    </Button>
                  </>
                )}

                {/* Main image */}
                <img
                  src={photos[selectedIndex]}
                  alt={`${eventTitle} photo ${selectedIndex + 1}`}
                  className="w-full h-auto max-h-[80vh] object-contain photo-signature"
                />

                {/* Photo counter */}
                {photos.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                    {selectedIndex + 1} / {photos.length}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        ))}
      </div>
    </Card>
  );
};

export default EventPhotoGallery;
